/**
 * The transform types the Transform screen offers, with their labels and the config a new step
 * starts from. Ported from the standalone Data Mapper's TransformPanel `TRANSFORM_OPTIONS` and
 * TransformStore `getDefaultConfig`; the type names and config shapes are the API engine's
 * (engine/types.ts), so a chain built here runs unchanged at import.
 */
import type { TransformConfig, TransformType, TypeBoxFieldType } from "./types";

export interface TransformTypeOption {
  type: TransformType;
  label: string;
  description: string;
}

export const TRANSFORM_TYPE_OPTIONS: readonly TransformTypeOption[] = [
  { type: "dateFormat", label: "Date Format", description: "Read a date in one format and write it in another (UTC)." },
  { type: "valueLookup", label: "Value Lookup", description: "Replace a value through a lookup table or an inline map." },
  { type: "concat", label: "Concatenate", description: "Join the mapped value with other source columns." },
  { type: "split", label: "Split", description: "Split on a separator and keep one part." },
  { type: "trim", label: "Trim", description: "Remove whitespace or listed characters from the ends." },
  { type: "uppercase", label: "Uppercase", description: "UPPER CASE." },
  { type: "lowercase", label: "Lowercase", description: "lower case." },
  { type: "capitalize", label: "Capitalize", description: "First letter upper, the rest lower." },
  { type: "typeCoercion", label: "Type Coercion", description: "Convert to string, number, integer, boolean or date." },
  { type: "regex", label: "Regex Replace", description: "Replace what a regular expression matches." },
  { type: "template", label: "Template", description: "Build text from {placeholders}." },
  { type: "default", label: "Default Value", description: "Use a fixed value when the input is null, empty or always." },
  { type: "custom", label: "Custom Code", description: "JavaScript, run in HQ's server sandbox at import time." },
];

export const transformTypeLabel = (type: string): string =>
  TRANSFORM_TYPE_OPTIONS.find((o) => o.type === type)?.label ?? type;

export const isTransformType = (type: unknown): type is TransformType =>
  TRANSFORM_TYPE_OPTIONS.some((o) => o.type === type);

/** The target types a Type Coercion step offers. */
export const COERCION_TARGET_TYPES: readonly TypeBoxFieldType[] = ["string", "number", "integer", "boolean", "date"];

/** Common date format presets; all output is rendered in UTC, so the literal 'Z' is accurate. */
export const DATE_FORMAT_PRESETS = [
  { label: "ISO 8601 (UTC)", format: "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'" },
  { label: "ISO Date", format: "yyyy-MM-dd" },
  { label: "US Date", format: "MM/dd/yyyy" },
  { label: "EU Date", format: "dd/MM/yyyy" },
  { label: "US DateTime", format: "MM/dd/yyyy HH:mm:ss" },
  { label: "Long Date", format: "MMMM d, yyyy" },
  { label: "Short Date", format: "M/d/yy" },
  { label: "SQL DateTime", format: "yyyy-MM-dd HH:mm:ss" },
  { label: "Unix Timestamp", format: "t" },
] as const;

/** The config a new step of this type starts from (the source's TransformStore.getDefaultConfig). */
export function defaultTransformConfig(type: TransformType): TransformConfig {
  switch (type) {
    case "dateFormat":
      return { type: "dateFormat", inputFormat: "MM/dd/yyyy", outputFormat: "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'" };
    case "valueLookup":
      return { type: "valueLookup", lookupMap: {}, caseSensitive: false };
    case "concat":
      // Start from the mapped value so adding the step never blanks data.
      return { type: "concat", fields: ["__value__"], separator: " " };
    case "split":
      return { type: "split", separator: " ", index: 0 };
    case "trim":
      return { type: "trim", trimType: "both" };
    case "uppercase":
    case "lowercase":
    case "capitalize":
      return { type };
    case "typeCoercion":
      return { type: "typeCoercion", targetType: "string" };
    case "regex":
      return { type: "regex", pattern: "", replacement: "", flags: "g" };
    case "template":
      return { type: "template", template: "{__value__}", fields: ["__value__"] };
    case "default":
      return { type: "default", value: null, condition: "null" };
    case "custom":
      return { type: "custom", code: "// value is the input value, row the source row\n// return the transformed value\nreturn value;" };
    default:
      throw new Error(`Unknown transform type: ${String(type)}`);
  }
}
